/*    
<summary>
   This class component is all about Managing store data functionality.
   Developer:Pratiksha Sharma & Co, Created Date:06-August-2025
</summary>
<param>No Parameter Passed</param>
<returns>Returns class instance</returns>
*/
import dayjs from "dayjs";
import { action, computed, makeObservable, observable } from "mobx";
import toast from "react-hot-toast";
import URLConstants from "../../constants/url-constants";
import { getBusinessTypeIdFromLocalStorage, getLanguageFromLocalStorage, getUserTypeFromLocalStorage } from "../../helpers/localstorage-helper";
import { IAddStoreCms, IUpdateStoreCms } from "../../models/forms/IAddStoreCms";
import { IObservableInitialState, IOption } from "../../models/ICommon";
import IApiResponse, {
  IApiSuccessResponse,
} from "../../models/response/IApiResponse";
import { IWorkingHoursSettingVM } from "../../models/response/IMasterManagementResponse";
import {
  ICMSBusinessTypeConditional,
  ICMSStoreListFilter,
  ICMSStoreListFilterDetails,
  ICMSStoreManagementList,
  IStaffCMSVM,
  IStoreCmsListVM,
  IStoreCmsVM,
  ITreatmentWithOptionVM,
} from "../../models/response/IStoreCmsResponse";
import { ICommonState } from "../../models/state/ICommonState";
import { formatMessage } from "../../translations/format-message";
import { initialState } from "../initialState/add-edit-store-cms-state";
import { initialState as GetAllStoreManagementState } from "../initialState/get-cms-store-list-state";
import * as baseService from "../service/base-service";
import { AuthStore } from "./auth-store";
import { IStoreManagementVM } from "../../models/response/IStoreManagementResponse";
import userTypeEnum from "../../constants/enums/user-type-enum";

const authStore = new AuthStore();

export class StoreCmsStore implements ICommonState {
  inProgress = false;
  error = "";

  initialStateValue: IObservableInitialState = {
    success: false,
    error: "",
    inProgress: false,
  };

  addUpdateStoreCmsState = { ...this.initialStateValue };

  storeData: IStoreManagementVM | undefined = undefined;
  storeList: IStoreCmsListVM = GetAllStoreManagementState;
  store: IStoreCmsVM | undefined = undefined;
  staff: IStaffCMSVM[] | undefined = undefined;
  cmsStoreWorkingHours: any[] | undefined = undefined;
  treatmentList: ITreatmentWithOptionVM[] | undefined = undefined;
  addUpdateCmsWorkingHoursState = { ...this.initialStateValue };
  cmsStoreState = { ...this.initialStateValue };
  cmsStoreStaffState = { ...this.initialStateValue };
  cmsStoreTreatmentState = { ...this.initialStateValue };
  cmsStoreWorkingHoursState = { ...this.initialStateValue };
  cmsBusinessTypeConditionalState = { ...this.initialStateValue };
  cmsBusinessTypeConditional: ICMSBusinessTypeConditional[] = [];
  initialFilter: ICMSStoreListFilterDetails = {
    StoreName: "",
    PrefectureName: "",
    BusinessTypeId: [],
  };
  
  cmsStoreListFilter: ICMSStoreListFilter = {
    ...this.initialFilter,
    isFilterActive: false,
  };
  cmsStoreAnonymousState = { ...this.initialStateValue };

  constructor() {
    makeObservable(this, {
      inProgress: observable,
      error: observable,
      store: observable,
      storeData: observable,
      staff: observable,
      storeList: observable,
      treatmentList: observable,
      addUpdateCmsWorkingHoursState: observable,
      cmsStoreStaffState: observable,
      cmsStoreTreatmentState: observable,
      cmsStoreWorkingHours: observable,
      cmsStoreWorkingHoursState: observable,
      addUpdateStoreCmsState: observable,
      cmsBusinessTypeConditionalState: observable,
      cmsBusinessTypeConditional: observable,
      cmsStoreState: observable,
      cmsStoreAnonymousState: observable,
      cmsStoreListFilter: observable,
      GetStoreDetailsByStoreCodeAnonymousService: action,
      AddStoreCmsService: action,
      UpdateStoreCmsService: action,
      GetStoreCmsByIdService: action,
      GetStoreCmsTreatmentsByIdService: action,
      GetStoreCmsStaffsByIdService: action,
      GetStoreCmsWorkingHoursByIdAnonymousService: action,
      GetStoreCmsByIdAnonymousService: action,
      AddCMSWorkingHoursService: action,
      UpdateCMSWorkingHoursService: action,
      GetCmsBusinessTypeConditionalService: action,
      GetStoreManagementListService: action,
      resetStoreCodeDetails: action,
      setCMSStoreListFilter: action,
      resetCMSStoreListFilter: action,
      resetCMSStoreAnonymousState: action,
      reset: action,
      resetStore: action,
      resetCMSStaffList: action,
      resetCMSStaffState: action,
      resetGetStoreStaffState: action,
      resetAddUpdateStoreCmsState: action,
      resetAddUpdateCMSWorkingHoursState: action,
      resetcmsBusinessTypeConditionalState: action,
      resetCMSTreatmentList: action,
      resetGetCMSStoreListState: action,
      resetcmsStoreTreatmentState: action,
      resetcmsStoreWorkingHoursState: action,
      cmsStoreDetails: computed,
      cmsStaffStoreList: computed,
      cmsTreatmentList: computed,
      getWorkingHoursSettingDetail: computed,
      getAllCMSBusinessTypeConditional: computed,
      allPrefectureOptions: computed,
      allCMSStoreManagementList: computed,
      storeCodeDetails: computed,
      allCMSStoreList: computed,
    });
  }

  /**
   * This function is used to Get All cms Store Managements List data with pagination information by calling an API.
   * @param currentPage : Current Page Number
   * @param pagerSize : Page Size
   * @returns
   */
  GetStoreManagementListService = (
    currentPage: number,
    pagerSize: number,
    filter: any
  ) => {
    this.inProgress = true;
    let businessTypeQuery = "";
    if (
      Array.isArray(filter.BusinessTypeId) &&
      filter.BusinessTypeId.length > 0
    ) {
      // This will produce "&businessTypeId=5&businessTypeId=1..." etc.
      businessTypeQuery = filter.BusinessTypeId.map(
        (id: number | string) => `&businessTypeId=${encodeURIComponent(id)}`
      ).join("");
    }
    const url =
      URLConstants.CMSStoreSideCustomerList +
      "?PageNo=" +
      currentPage +
      "&PageSize=" +
      pagerSize +
      businessTypeQuery +
      "&storeName=" +
      filter.StoreName +
      "&prefectureName=" +
      (filter.PrefectureName === "please_select" ? "" : filter.PrefectureName) +
      "&column=" +
      "store_name" +
      "&orderBy=" +
      "asc";

    // this.storeList = demoStoreData;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IStoreCmsListVM>>) => {
        if (response.data.Error) {
          this.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.storeList = response.data.Data;
        }
      })
      .catch((err: string) => {
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  /**
   * This function is used to map storeList to allStoreManagement List suitable for Grid Component.
   * @returns allStoreManagementsList
   */
  get allCMSStoreManagementList(): ICMSStoreManagementList[] {
    if (this.storeList?.StoreVM && this.storeList.StoreVM?.length > 0)
      return this.storeList.StoreVM.map((store) => {
        let language = getLanguageFromLocalStorage();
        return {
          Id: store.Id,
          StoreName: store.StoreName,
          Email: store.Email,
          MobileNumber: store.MobileNumber,
          Address: store.Address,
          StoreDetails: store.StoreDetails,
          StoreMapURL: store.StoreMapURL,
          StoreCode: store.StoreCode,
          AccessInformation: store.AccessInformation,
          PhoneMessageVisible: store.PhoneMessageVisible,
          StorePhoneMessage: store.StorePhoneMessage,
          Image: store.Image,
          Language: language,
        };
      });
    return [];
  }

    get allCMSStoreList(): ICMSStoreManagementList[] {
    if (this.storeList?.StoreVM && this.storeList.StoreVM?.length > 0)
      return this.storeList.StoreVM.map((store) => {
        let language = getLanguageFromLocalStorage();
        return {
          Id: store.Id,
          StoreName: store.StoreName,
          Email: store.Email,
          MobileNumber: store.MobileNumber,
          Address: store.Address,
          StoreDetails: store.StoreDetails,
          StoreMapURL: store.StoreMapURL,
          StoreCode: store.StoreCode,
          AccessInformation: store.AccessInformation,
          PhoneMessageVisible: store.PhoneMessageVisible,
          StorePhoneMessage: store.StorePhoneMessage,
          ShowOnReservationSite: store.ShowOnReservationSite,
          Image: store.Image,
          Language: language,
        };
      });
    return [];
  }

  /**
   * This function is used to Get StoreCms Details by Id by calling an API.
   * @param id : The StoreCms Identifier
   * @returns
   */
  GetStoreCmsByIdService = (id: number) => {
    this.cmsStoreState.inProgress = true;
    let url = `${URLConstants.StoreCms}?StoreId=${id}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IStoreCmsVM>>) => {
        if (response.data.Error) {
          this.cmsStoreState.error = response.data.Message;
        } else {
          let data = response.data.Data;
          this.store = data;
        }
      })
      .catch((err: string) => {
        this.cmsStoreState.error = err;
      })
      .finally(
        action(() => {
          this.cmsStoreState.inProgress = false;
        })
      );
  };

  GetStoreCmsByIdAnonymousService = (id: number) => {
    this.cmsStoreState.inProgress = true;
    let url = `${URLConstants.StoreCmsAnonymous}?StoreId=${id}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IStoreCmsVM>>) => {
        if (response.data.Error) {
          this.cmsStoreState.error = response.data.Message;
        } else {
          let data = response.data.Data;
          this.store = data;
        }
      })
      .catch((err: string) => {
        this.cmsStoreState.error = err;
      })
      .finally(
        action(() => {
          this.cmsStoreState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to Get StoreCms Details by Id by calling an API.
   * @param id : The StoreCms Identifier
   * @returns
   */
  GetStoreCmsStaffsByIdService = (id: number) => {
    this.cmsStoreStaffState.inProgress = true;
    let url = `${URLConstants.GetCMSStaffListByStoreId}?storeId=${id}&isCustomerLogin=true`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IStaffCMSVM[]>>) => {
        if (response.data.Error) {
          this.cmsStoreStaffState.error = response.data.Message;
        } else {
          let data = response.data.Data;
          this.staff = data;
        }
      })
      .catch((err: string) => {
        this.cmsStoreStaffState.error = err;
      })
      .finally(
        action(() => {
          this.cmsStoreStaffState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to Get StoreCms Details by Id by calling an API.
   * @param id : The StoreCms Identifier
   * @returns
   */
  GetStoreCmsTreatmentsByIdService = (id: number, isFeatured: boolean) => {
    this.cmsStoreTreatmentState.inProgress = true;
    let url = `${URLConstants.GetCMSTreatmentListByStoreId}?storeId=${id}&isFeatured=${isFeatured}`;
    return baseService
      .getRequest(url)
      .then(
        (
          response: IApiResponse<IApiSuccessResponse<ITreatmentWithOptionVM[]>>
        ) => {
          if (response.data.Error) {
            this.cmsStoreTreatmentState.error = response.data.Message;
          } else {
            this.treatmentList = response.data.Data;
          }
        }
      )
      .catch((err: string) => {
        this.cmsStoreTreatmentState.error = err;
      })
      .finally(
        action(() => {
          this.cmsStoreTreatmentState.inProgress = false;
        })
      );
  };

  resetcmsStoreTreatmentState = () =>{
    this.cmsStoreTreatmentState = {...this.initialStateValue};
    this.treatmentList = undefined;
  }
  /**
   * This function is used to Add New StoreCms by calling an API.
   * @param data : StoreCms Data
   * @returns
   */
  AddStoreCmsService = (data: IAddStoreCms) => {
    this.addUpdateStoreCmsState.inProgress = true;
    let url = URLConstants.StoreCms;
    const formData = new FormData();
    formData.append("StoreName", data.StoreName);
    formData.append("Email", data.Email.toLowerCase());
    formData.append("MobileNumber", String(data.MobileNumber));
    formData.append("Address", data.Address);
    formData.append("StoreDetails", data.StoreDetails);
    formData.append("Url", data.StoreMapURL);
    formData.append("AccessInformation", data.AccessInformation);
    formData.append("StorePhoneMessage", data.StorePhoneMessage);
    formData.append("PhoneMessageVisible", String(data.PhoneMessageVisible));

    return baseService
      .postMultipart(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateStoreCmsState.error = response.data.Message;
        } else this.addUpdateStoreCmsState.success = true;
      })
      .catch((err: string) => {
        this.addUpdateStoreCmsState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateStoreCmsState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to Updating Existing StoreCms Details by calling an API.
   * @param id : The StoreCms Identifier
   * @param data
   * @returns
   */
  UpdateStoreCmsService = (data: IUpdateStoreCms) => {
    this.addUpdateStoreCmsState.inProgress = true;
    let url = URLConstants.UpdateStoreCms;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.addUpdateStoreCmsState.error = response.data.Message;
        } else this.addUpdateStoreCmsState.success = true;
      })
      .catch((err: string) => {
        this.addUpdateStoreCmsState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateStoreCmsState.inProgress = false;
        })
      );
  };

  GetCmsBusinessTypeConditionalService = (showOnFilter: boolean) => {
    this.cmsBusinessTypeConditionalState.inProgress = true;
    let url = `${URLConstants.CmsBusinessTypeConditional}?ShowOnFilter=${showOnFilter}`;
    return baseService
      .getRequest(url)
      .then(
        (
          response: IApiResponse<
            IApiSuccessResponse<ICMSBusinessTypeConditional[]>
          >
        ) => {
          if (response.data.Error) {
            this.cmsBusinessTypeConditionalState.error = response.data.Message;
          } else {
            let data = response.data.Data;
            this.cmsBusinessTypeConditional = data;
          }
        }
      )
      .catch((err: string) => {
        this.cmsBusinessTypeConditionalState.error = err;
      })
      .finally(
        action(() => {
          this.cmsBusinessTypeConditionalState.inProgress = false;
        })
      );
  };

  get getAllCMSBusinessTypeConditional(): IOption[] {
    const options: IOption[] = [];

    if (
      this.cmsBusinessTypeConditional &&
      this.cmsBusinessTypeConditional.length > 0
    ) {
      this.cmsBusinessTypeConditional.map(
        (option: ICMSBusinessTypeConditional) => {
          options.push({
            id: option.Id,
            value: option.TypeName,
            label: option.TypeName,
            disabled: false,
            data: { ...option },
          });
        }
      );
    }
    return options;
  }

  get allPrefectureOptions(): IOption[] {
    const options: IOption[] = [
      {
        id: -1,
        value: "please_select",
        label: "please_select",
        disabled: false,
      },
    ];

    if (
      this.cmsBusinessTypeConditional &&
      this.cmsBusinessTypeConditional.length > 0
    ) {
      this.cmsBusinessTypeConditional.map(
        (option: ICMSBusinessTypeConditional) => {
          options.push({
            id: option.Id,
            value: option.TypeName,
            label: option.TypeName,
            disabled: false,
            data: { ...option },
          });
        }
      );
    }
    return options;
  }

  resetcmsBusinessTypeConditionalState = () => {
    this.cmsBusinessTypeConditionalState = { ...this.initialStateValue };
    this.cmsBusinessTypeConditional = [];
  };

  resetCMSStaffState = () => {
    this.cmsStoreStaffState = { ...this.initialStateValue };
    this.staff = undefined;
  };

  resetCMSStaffList = () => {
    this.staff = [];
  };
  /*
    This function is used to reset all AddStoreCms observables to their initial values.  
    */
  resetAddUpdateStoreCmsState = () => {
    this.addUpdateStoreCmsState.inProgress = false;
    this.addUpdateStoreCmsState.success = false;
    this.addUpdateStoreCmsState.error = "";
  };

  resetGetStoreStaffState = () => {
    this.staff = undefined;
    this.cmsStoreStaffState = { ...this.initialStateValue };
  };

  /*
    This function is used to reset all observables to their initial values.  
    */
  resetGetStoreCmsState = () => {
    this.error = "";
    this.inProgress = false;
    this.cmsStoreState = { ...this.initialStateValue };
  };

  resetGetCMSStoreListState = () => {
    this.error = "";
    this.inProgress = false;
    this.storeList = GetAllStoreManagementState;
  };

  /*
    This function is used to reset all observables to their initial values.  
    */
  reset = () => {
    this.error = "";
    this.inProgress = false;
    this.addUpdateStoreCmsState = { ...this.initialStateValue };
    this.cmsStoreState = { ...this.initialStateValue };
  };

  /**
   * This function is used to reset all store observables to their initial values.
   * @returns
   */
  resetStore = () => {
    this.error = "";
    this.store = undefined;
    this.inProgress = false;
    this.addUpdateStoreCmsState = { ...this.initialStateValue };
    this.cmsStoreState = { ...this.initialStateValue };
  };

  get cmsStoreDetails(): IStoreCmsVM {
    if (this.store) {
      return {
        Id: this.store.Id,
        StoreName: this.store.StoreName ?? "",
        Email: this.store.Email ?? "",
        MobileNumber: this.store.MobileNumber ?? "",
        Address: this.store.Address ?? "",
        StoreDetails: this.store.StoreDetails ?? "",
        StoreCode: this.store.StoreCode ?? "",
        StoreMapURL: this.store.StoreMapURL ?? "",
        AccessInformation: this.store.AccessInformation ?? "",
        StorePhoneMessage: this.store.StorePhoneMessage ?? "",
        PhoneMessageVisible: this.store.PhoneMessageVisible,
      };
    } else {
      return initialState;
    }
  }

  get cmsStaffStoreList(): IStaffCMSVM[] {
    if (this.staff && this.staff?.length > 0) {
      return this.staff?.map((staff) => ({
        Id: staff.Id,
        StoreId: staff.StoreId,
        StaffName: staff.StaffName,
        Email: staff.Email,
        UserType: staff.UserType,
        AccountStatus: staff.AccountStatus,
        TelephoneNumber: staff.TelephoneNumber,
        PostCode: staff.PostCode,
        NumberOfAvailability: staff.NumberOfAvailability,
        NominationFee: staff.NominationFee,
        IsNominationFee: staff.IsNominationFee,
        Image: staff.Image,
        StoreName: staff.StoreName,
        PrefectureName: staff.PrefectureName,
        Address: staff.Address,
        Gender: staff.Gender,
        Expertise: staff.Expertise,
        Introduction: staff.Introduction,
        OrderId: staff.OrderId,
        TotalCount: staff.TotalCount,
        StoreCode: staff.StoreCode,
        AreaCode: staff.AreaCode,
        HeadquarterId: staff.HeadquarterId,
        Password: staff.Password,
        TotalReservations: staff.TotalReservations,
        TotalBookingTime: staff.TotalBookingTime,
        ShowOnReservationSite: staff.ShowOnReservationSite,
      }));
    } else {
      return [];
    }
  }

  get cmsTreatmentList(): ITreatmentWithOptionVM[] {
    if (this.treatmentList && this.treatmentList?.length > 0) {
      return this.treatmentList.map((treatment) => ({
        TreatmentId: treatment.TreatmentId,
        CategoryId: treatment.CategoryId,
        TreatmentName: treatment.TreatmentName,
        Image: treatment.Image,
        Description: treatment.Description,
        FeeIncludingTax: treatment.FeeIncludingTax,
        FeeExcludingTax: treatment.FeeExcludingTax,
        TaxAmount: treatment.TaxAmount,
        TaxId: treatment.TaxId,
        TaxValue: treatment.TaxValue,
        OptionMenu: treatment.OptionMenu,
        Options: treatment.Options,
      }));
    } else {
      return [];
    }
  }

  AddCMSWorkingHoursService = (data: any) => {
    this.addUpdateCmsWorkingHoursState.inProgress = true;
    let url = URLConstants.AddCMSWorkingHours;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.addUpdateCmsWorkingHoursState.error = response.data.Message;
        } else this.addUpdateCmsWorkingHoursState.success = true;
      })
      .catch((err: string) => {
        this.addUpdateCmsWorkingHoursState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateCmsWorkingHoursState.inProgress = false;
        })
      );
  };

  UpdateCMSWorkingHoursService = (data: any) => {
    this.addUpdateCmsWorkingHoursState.inProgress = true;
    let url = URLConstants.UpdateCMSWorkingHours;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.addUpdateCmsWorkingHoursState.error = response.data.Message;
        } else this.addUpdateCmsWorkingHoursState.success = true;
      })
      .catch((err: string) => {
        this.addUpdateCmsWorkingHoursState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateCmsWorkingHoursState.inProgress = false;
        })
      );
  };

  resetAddUpdateCMSWorkingHoursState = () => {
    this.addUpdateCmsWorkingHoursState = { ...this.initialStateValue };
  };

  GetStoreCmsWorkingHoursByIdAnonymousService = (id: number) => {
    this.cmsStoreWorkingHoursState.inProgress = true;
    let url = `${URLConstants.GetCMSWorkingHoursByStoreId}/${id}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any[]>>) => {
        if (response.data.Error) {
          this.cmsStoreWorkingHoursState.error = response.data.Message;
        } else {
          let data = response.data.Data;
          this.cmsStoreWorkingHours = data;
        }
      })
      .catch((err: string) => {
        this.cmsStoreWorkingHoursState.error = err;
      })
      .finally(
        action(() => {
          this.cmsStoreWorkingHoursState.inProgress = false;
        })
      );
  };

  resetcmsStoreWorkingHoursState = () =>{
    this.cmsStoreWorkingHoursState = {...this.initialStateValue};
    this.cmsStoreWorkingHours = undefined;
  }

  get getWorkingHoursSettingDetail(): any | null {
    if (!this.cmsStoreWorkingHours)
      return {
        Id: -1,
        IsVisible: "0",
        HolidaysMessage: "",
        IsHolidayVisible: false,
        BusinessSettings: Array.from({ length: 8 }, (_, index) => ({
          WeekDays: index,
          IsClosed: false,
          IsVisible: false,
          OpenTime: "00:00",
          ClosedTime: "00:00",
        })),
      };

    return {
      Id:
        this.cmsStoreWorkingHours[0]?.Id > 0
          ? this.cmsStoreWorkingHours[0]?.Id
          : -1,
      IsVisible:
        this.cmsStoreWorkingHours?.length > 0
          ? this.cmsStoreWorkingHours?.find((item) => item.WeekDays == 9)
            ? this.cmsStoreWorkingHours?.find((item) => item.WeekDays == 9)
                ?.IsVisible == true
              ? "1"
              : "0"
            : "0"
          : "0",
      HolidaysMessage:
        this.cmsStoreWorkingHours?.length > 0
          ? this.cmsStoreWorkingHours?.find((item) => item.WeekDays == 8)
            ? this.cmsStoreWorkingHours?.find((item) => item.WeekDays == 8)
                ?.HolidayMessage
            : ""
          : "",
      IsHolidayVisible:
        this.cmsStoreWorkingHours?.length > 0
          ? this.cmsStoreWorkingHours?.find((item) => item.WeekDays == 8)
            ? this.cmsStoreWorkingHours?.find((item) => item.WeekDays == 8)
                ?.IsVisible
            : false
          : false,
      BusinessSettings:
        Array.isArray(this.cmsStoreWorkingHours) &&
        this.cmsStoreWorkingHours?.length > 0
          ? this.cmsStoreWorkingHours
              .filter((item) => item.WeekDays !== 8 && item.WeekDays !== 9)
              ?.map((setting: IWorkingHoursSettingVM) => {
                return {
                  WeekDays: setting.WeekDays,
                  IsClosed: setting.IsClosed,
                  IsVisible: setting?.WeekDays == 7 ? setting.IsVisible : true,
                  OpenTime: setting.OpenTime
                    ? dayjs(setting.OpenTime, "HH:mm:ss").format("HH:mm")
                    : "00:00",
                  ClosedTime: setting.ClosedTime
                    ? dayjs(setting.ClosedTime, "HH:mm:ss").format("HH:mm")
                    : "00:00",
                };
              })
          : Array.from({ length: 8 }, (_, index) => ({
              WeekDays: index,
              IsClosed: false,
              IsVisible: false,
              OpenTime: "00:00",
              ClosedTime: "00:00",
            })),
    };
  }

  setCMSStoreListFilter = (filter: ICMSStoreListFilter) => {
    this.cmsStoreListFilter = { ...filter };
  };

  resetCMSStoreListFilter = () => {
    this.cmsStoreListFilter = {
      ...this.initialFilter,
      isFilterActive: false,
    };
  };

  resetCMSTreatmentList = () => {
    this.cmsStoreTreatmentState = {...this.initialStateValue};
    this.treatmentList = [];
  };

  GetStoreDetailsByStoreCodeAnonymousService = (storeCode: string) => {
    this.cmsStoreAnonymousState.inProgress = true;
    let url = `${URLConstants.StoreCmsByStoreCodeAnonymous}?StoreCode=${storeCode}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.cmsStoreAnonymousState.error = response.data.Message;
        } else {
          let data = response.data.Data;
          this.storeData = data;
          this.cmsStoreAnonymousState.error = "";
        }
      })
      .catch((err: string) => {
        this.cmsStoreAnonymousState.error = err;
      })
      .finally(
        action(() => {
          this.cmsStoreAnonymousState.inProgress = false;
        })
      );
  };

  resetCMSStoreAnonymousState = () =>{
    this.cmsStoreAnonymousState = {...this.initialStateValue};
  }

  get storeCodeDetails() {
    if (this.storeData) {
      return {
        Id: this.storeData.Id,
        StoreName: this.storeData.StoreName ? this.storeData.StoreName : "",
        StoreCode: this.storeData.StoreCode ?? "",
        BusinessTypeId: this.storeData.BusinessTypeId
          ? this.storeData.BusinessTypeId
          : -1,
        MobileNumber: this.storeData.MobileNumber ? this.storeData.MobileNumber : "",
        Email: this.storeData.Email ? this.storeData.Email.toLowerCase() : "",
        PrefectureName: this.storeData.PrefectureName ? this.storeData.PrefectureName : "",
        PostalCode: this.storeData.PostalCode ? this.storeData.PostalCode : "",
        StoreOwnerName: this.storeData.StoreOwnerName
          ? this.storeData.StoreOwnerName
          : "",
        Address: this.storeData.Address ? this.storeData?.PrefectureName ? this.storeData?.PrefectureName + this.storeData.Address : this.storeData.Address : "",
        Url: this.storeData.Url ? this.storeData.Url : "",
        KanaName: this.storeData.KanaName ? this.storeData.KanaName : "",
        Status: this.storeData.Status ? this.storeData.Status.toString() : "0",
        IsNominationFee: this.storeData.IsNominationFee === true ? "1" : "0",
        StoreCategory: this.storeData.StoreCategory.toString(),
        StoreLink: this.storeData.StoreLink ? this.storeData.StoreLink : "",
        Image: this.storeData.Image ? this.storeData.Image : "",
        HeadquarterId: this.storeData.HeadquarterId,
        ThumbnailImage: this.storeData.ThumbnailImage
          ? this.storeData.ThumbnailImage
          : "",
        UserId: this.storeData.Users ? this.storeData.Users.Id : 0,
      };
    }
    return {
      ...initialState,
      BusinessTypeId:
        getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.HeadQuarter]
          ? getBusinessTypeIdFromLocalStorage()
          : -1,
    };
  }

  resetStoreCodeDetails = () => {
    this.storeData = undefined;
  };
}

export default new StoreCmsStore();
